/**
 * Insights Timeline - Version 2.0
 * Chargement depuis Supabase, filtres avancés, virtual scrolling
 * Optimisé pour des millions d'insights
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Sparkles,
  Download,
  Loader2,
  AlertCircle,
  TrendingUp,
  BarChart3,
  RefreshCw,
  ArrowUpDown
} from 'lucide-react';
import { BigDataDataset } from '../../services/BigDataDatasetService';
import BigDataInsightsService, {
  BigDataInsight,
  InsightFilters as InsightFiltersType,
  InsightSortOptions
} from '../../services/BigDataInsightsService';
import { useAuth } from '../auth/AuthProvider';
import InsightCard from './InsightCard';
import InsightFilters from './InsightFilters';
import { useBigDataInsightsRealtime } from '../../hooks/useBigDataChatRealtime';
import { debugLog } from '../../utils/debug-logger';
import logger from '../../utils/logger';

interface InsightsTimelineProps {
  dataset: BigDataDataset;
}

const InsightsTimeline: React.FC<InsightsTimelineProps> = ({ dataset }) => {
  const { user } = useAuth();
  const [insights, setInsights] = useState<BigDataInsight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [totalInsights, setTotalInsights] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const pageSize = 50;
  
  // Filtres
  const [filters, setFilters] = useState<InsightFiltersType>({});
  const [sortOptions, setSortOptions] = useState<InsightSortOptions>({
    sortBy: 'discovered_at',
    sortOrder: 'DESC'
  });
  
  // Stats
  const [stats, setStats] = useState<any>(null);
  
  // Virtual scroll
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // ✅ REALTIME SUPABASE - Auto-refresh quand nouveaux insights
  const { isConnected } = useBigDataInsightsRealtime(
    dataset.id,
    user?.id || '',
    (newInsight) => {
      debugLog('[🔥 Realtime] New insight detected:', newInsight.title);
      
      // Ajouter l'insight au début de la liste (si pas de filtres actifs)
      if (!filters.types && !filters.categories && !filters.search) {
        setInsights(prev => [newInsight as BigDataInsight, ...prev]);
        setTotalInsights(prev => prev + 1);
      } else {
        // Si filtres actifs, recharger pour maintenir cohérence
        loadInsights(0, false);
      }
      
      // Recharger stats
      loadStats();
    }
  );

  // Log connection status
  useEffect(() => {
    if (isConnected) {
      debugLog('[✅ Realtime] Insights Timeline connected for dataset:', dataset.id);
    }
  }, [isConnected, dataset.id]);

  // Charger les insights
  const loadInsights = useCallback(async (page: number = 0, append: boolean = false) => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      debugLog('[💡 InsightsTimeline] Loading insights...', { page, filters, sortOptions });
      
      const result = await BigDataInsightsService.getDatasetInsights(
        dataset.id,
        user.id,
        filters,
        sortOptions,
        page,
        pageSize
      );
      
      setInsights(prev => append ? [...prev, ...result.insights] : result.insights);
      setTotalInsights(result.total);
      setHasMore(result.hasMore);
      setCurrentPage(page);
      
      debugLog('[✅ InsightsTimeline] Loaded insights:', result.insights.length);
    } catch (err) {
      logger.error('[❌ InsightsTimeline] Failed to load insights:', err);
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [user, dataset.id, filters, sortOptions]);

  // Charger les stats
  const loadStats = useCallback(async () => {
    try {
      const stats = await BigDataInsightsService.getDatasetStats(dataset.id);
      setStats(stats);
      debugLog('[📊 InsightsTimeline] Stats loaded:', stats);
    } catch (err) {
      logger.warn('[⚠️ InsightsTimeline] Failed to load stats:', err);
    }
  }, [dataset.id]);

  // Initial load
  useEffect(() => {
    loadInsights(0, false);
    loadStats();
  }, [loadInsights, loadStats]);

  // Infinite scroll setup
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loading) {
        debugLog('[📜 InsightsTimeline] Loading more...');
        loadInsights(currentPage + 1, true);
      }
    });

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [hasMore, loading, currentPage, loadInsights]);

  // Handlers
  const handleFiltersChange = (newFilters: InsightFilters) => {
    setFilters(newFilters);
    setCurrentPage(0);
  };

  const handleClearFilters = () => {
    setFilters({});
    setCurrentPage(0);
  };

  const handleSortChange = (newSort: InsightSortOptions) => {
    setSortOptions(newSort);
    setCurrentPage(0);
  };

  const handleBookmark = async (insightId: string) => {
    if (!user) return;
    try {
      await BigDataInsightsService.toggleBookmark(insightId, user.id);
      // Refresh
      loadInsights(0, false);
    } catch (err) {
      logger.error('[❌ InsightsTimeline] Failed to toggle bookmark:', err);
    }
  };

  const handleValidate = async (insightId: string) => {
    if (!user) return;
    try {
      await BigDataInsightsService.toggleValidation(insightId, user.id);
      // Refresh
      loadInsights(0, false);
    } catch (err) {
      logger.error('[❌ InsightsTimeline] Failed to toggle validation:', err);
    }
  };

  const handleExportAll = async () => {
    try {
      const blob = await BigDataInsightsService.exportInsights(dataset.id, user!.id, filters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `insights-${dataset.file_name}-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      logger.error('[❌ InsightsTimeline] Export failed:', err);
    }
  };

  // Loading state
  if (loading && currentPage === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-purple-500" />
          <p className="text-purple-900 dark:text-white font-medium">Chargement des insights...</p>
          <p className="text-sm text-purple-600 dark:text-gray-400 mt-2">Analyse en cours</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-12 text-center">
        <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-red-400 mb-2">
          Erreur de chargement
        </h3>
        <p className="text-red-300 mb-4">{error}</p>
        <button
          onClick={() => loadInsights(0, false)}
          className="px-6 py-3 bg-red-500/20 text-red-400 rounded-xl font-semibold hover:bg-red-500/30 transition-colors inline-flex items-center space-x-2"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Réessayer</span>
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header avec Stats */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center space-x-3">
            <Sparkles className="w-8 h-8 text-purple-500" />
            <div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-700 to-pink-700 dark:from-white dark:to-white bg-clip-text text-transparent">
                Découvertes IA
              </h2>
              <p className="text-purple-600 dark:text-gray-400 font-medium">
                {totalInsights.toLocaleString()} insight{totalInsights > 1 ? 's' : ''} trouvé{totalInsights > 1 ? 's' : ''}
              </p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          {/* Sort */}
          <select
            value={`${sortOptions.sortBy}_${sortOptions.sortOrder}`}
            onChange={(e) => {
              const [sortBy, sortOrder] = e.target.value.split('_') as [any, 'ASC' | 'DESC'];
              handleSortChange({ sortBy, sortOrder });
            }}
            className="px-4 py-2 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-slate-800 dark:to-slate-800 border-2 border-purple-200 dark:border-slate-700 rounded-lg text-purple-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-400 shadow-sm"
          >
            <option value="discovered_at_DESC">Plus récents</option>
            <option value="discovered_at_ASC">Plus anciens</option>
            <option value="confidence_DESC">Confiance (haute)</option>
            <option value="confidence_ASC">Confiance (basse)</option>
            <option value="impact_score_DESC">Impact (haut)</option>
            <option value="importance_DESC">Importance</option>
          </select>

          {/* Export */}
          <button
            onClick={handleExportAll}
            className="px-4 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-slate-800 dark:to-slate-800 hover:from-blue-100 hover:to-indigo-100 dark:hover:from-slate-700 dark:hover:to-slate-700 border-2 border-blue-200 dark:border-slate-700 rounded-lg text-blue-900 dark:text-white transition-all shadow-sm inline-flex items-center space-x-2"
          >
            <Download className="w-4 h-4" />
            <span className="font-medium">Exporter</span>
          </button>

          {/* Refresh */}
          <button
            onClick={() => loadInsights(0, false)}
            className="p-2 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-slate-800 dark:to-slate-800 hover:from-purple-100 hover:to-pink-100 dark:hover:from-slate-700 dark:hover:to-slate-700 border-2 border-purple-200 dark:border-slate-700 rounded-lg text-purple-900 dark:text-white transition-all shadow-sm"
            title="Rafraîchir"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Stats Dashboard */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-slate-800 dark:to-slate-800 rounded-xl p-4 border border-purple-200 dark:border-transparent shadow-sm">
            <div className="text-purple-700 dark:text-gray-400 text-sm mb-1 font-medium">Total</div>
            <div className="text-2xl font-bold text-purple-900 dark:text-white">{stats.total_insights || 0}</div>
          </div>
          <div className="bg-red-500/10 rounded-xl p-4">
            <div className="text-red-300 text-sm mb-1">Critiques</div>
            <div className="text-2xl font-bold text-red-400">{stats.critical_count || 0}</div>
          </div>
          <div className="bg-purple-500/10 rounded-xl p-4">
            <div className="text-purple-300 text-sm mb-1">Haute valeur</div>
            <div className="text-2xl font-bold text-purple-400">{stats.high_value_count || 0}</div>
          </div>
          <div className="bg-green-500/10 rounded-xl p-4">
            <div className="text-green-300 text-sm mb-1">Validés</div>
            <div className="text-2xl font-bold text-green-400">{stats.validated_count || 0}</div>
          </div>
        </div>
      )}

      {/* Filtres */}
      <InsightFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onClear={handleClearFilters}
        totalInsights={totalInsights}
        filteredInsights={insights.length}
      />

      {/* Insights List avec Virtual Scrolling */}
      <div className="space-y-4">
        {insights.length === 0 ? (
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-slate-800 dark:to-slate-800 border border-purple-200 dark:border-slate-700 rounded-2xl p-12 text-center">
            <Sparkles className="w-16 h-16 text-purple-600 dark:text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-purple-900 dark:text-white mb-2">
              Aucun insight pour le moment
            </h3>
            <p className="text-purple-700 dark:text-gray-400">
              Les insights seront découverts automatiquement pendant l'analyse
            </p>
          </div>
        ) : (
          <>
            {insights.map((insight) => (
              <InsightCard
                key={insight.id}
                insight={insight}
                onBookmark={() => handleBookmark(insight.id)}
                onValidate={() => handleValidate(insight.id)}
                onNavigate={() => {
                  // TODO: Navigate to grid
                  debugLog('[Navigate] Go to row', insight.row_start);
                }}
                onExport={() => {
                  // TODO: Export single insight
                  debugLog('[Export] Insight', insight.id);
                }}
              />
            ))}

            {/* Load More Trigger */}
            {hasMore && (
              <div ref={loadMoreRef} className="py-8 text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-purple-500" />
                <p className="text-sm text-purple-700 dark:text-gray-400 mt-2">Chargement...</p>
              </div>
            )}

            {/* End Message */}
            {!hasMore && insights.length > 0 && (
              <div className="text-center py-8">
                <p className="text-purple-700 dark:text-gray-400">
                  Tous les insights ont été chargés ({totalInsights.toLocaleString()})
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default InsightsTimeline;

